import { useCallback, useMemo, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import type { SiteQueryContainerQuery } from '../__generated__/SiteQueryContainerQuery.graphql';
import type { VisitorInfoType } from '../types/visitorInfo';
import { getEffectiveVisitorInfoType, getStorageKey } from '../types/visitorInfo';
import type { ILocationDescriptor } from './FundsService';
import Site from './Site';

/** Maps the route-level visitorInfoType to the GraphQL SpecialPage enum value. */
function getSpecialPageId(type: VisitorInfoType): 'VISITOR_INFO' | 'PUBLICATIONS_VISITOR_INFO' {
  if (type === 'publications') {
    return 'PUBLICATIONS_VISITOR_INFO';
  }
  return 'VISITOR_INFO';
}

// Re-export the SiteQuery for use in routing
export const SiteQuery = graphql`
    query SiteQueryContainerQuery($path: String!, $visitorInfo: VisitorInfo, $language: String!, $salesAdmission: String!, $searchString: String, $specialPageId: SpecialPage!) {
        site {
            ...Site_site @arguments(path: $path, visitorInfo: $visitorInfo, language: $language, salesAdmission: $salesAdmission, searchString: $searchString, specialPageId: $specialPageId)
        }
        user {
            ...Site_user
        }
    }
`;

interface VisitorInfoDataType {
  country: 'at' | 'de' | null;
  code: 'private' | 'business';
}

interface SiteQueryContainerProps {
  routeProps: {
    location: ILocationDescriptor;
    params?: Record<string, string>;
  };
  lang: string;
  visitorInfoType: VisitorInfoType;
  needsNavigation: boolean;
  isIndex?: boolean;
}

/**
 * Container component that fetches the root Site query using useLazyLoadQuery.
 * This replaces the legacy QueryRenderer pattern with modern Relay hooks.
 *
 * The component:
 * 1. Computes query variables from route props
 * 2. Fetches data using useLazyLoadQuery (suspends while loading)
 * 3. Passes fetched data to the Site component
 */
export function SiteQueryContainer({ routeProps, lang, visitorInfoType, needsNavigation, isIndex = false }: SiteQueryContainerProps): React.ReactElement {
  // A bare ISIN URL (e.g. /AT0000A1TVM9) is a fund page but can only be matched
  // by the generic `:path` catch-all under React Router v6, which is typed
  // "none". Upgrade it to "funds" so the disclaimer gate and special page behave
  // exactly like the dedicated fund routes. Non-ISIN paths keep their declared type.
  const effectiveVisitorInfoType = getEffectiveVisitorInfoType(routeProps.location.pathname, visitorInfoType);

  // The visitor-info values below are read from sessionStorage, which is mutated
  // (not React state) by VisitorInfoPage.onConfirm. Bumping this revision after a
  // confirm forces both reads to recompute so the query variable changes and the
  // Relay query re-fetches with the freshly provided visitor info.
  const [visitorInfoRevision, setVisitorInfoRevision] = useState(0);
  const refreshVisitorInfo = useCallback(() => setVisitorInfoRevision((revision) => revision + 1), []);

  // Always read the fund-route visitor info from sessionStorage for the GraphQL
  // query variable. The server uses this to decide page access (e.g., returning
  // FundsDetailsPage vs DeniedPage). This must be sent regardless of
  // visitorInfoType because routes like `:path` (visitorInfoType="none") can
  // still resolve to fund pages on the server (e.g., /AT0000A1TVM9).
  //
  // The deps are intentional re-read triggers for the non-reactive sessionStorage:
  // `pathname` refreshes on navigation, `visitorInfoRevision` after a confirm.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname + visitorInfoRevision are sessionStorage re-read triggers, not values used in the closure.
  const queryVisitorInfo = useMemo<VisitorInfoDataType | null>(() => {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    const stored = sessionStorage.getItem('visitorInfo');
    return stored ? JSON.parse(stored) : null;
  }, [routeProps.location.pathname, visitorInfoRevision]);

  // Read the route-specific visitor info for the gating decision (whether to
  // show the disclaimer form). For "funds" routes this reads from 'visitorInfo',
  // for "publications" routes from 'publicationsVisitorInfo', for "none" it's null.
  // `visitorInfoRevision` is an intentional trigger to re-read after a confirm.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname + visitorInfoRevision are sessionStorage re-read triggers, not values used in the closure.
  const gatingVisitorInfo = useMemo<VisitorInfoDataType | null>(() => {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    const storageKey = getStorageKey(effectiveVisitorInfoType);
    if (!storageKey) {
      return null;
    }
    const stored = sessionStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : null;
  }, [routeProps.location.pathname, effectiveVisitorInfoType, visitorInfoRevision]);

  // Compute query variables from route props
  const variables = useMemo(() => {
    let path = routeProps.location.pathname.replace(/^\/|\/$/g, '');

    // For index routes, append 'Front' to the path
    if (isIndex) {
      // If path is empty (root URL), use just 'Front'
      // If path has content (e.g., 'de'), use path/Front
      path = path.length > 0 ? `${path}/Front` : 'Front';
    }

    const language = lang.replace(/^\/|\/$/g, '') || 'de';

    return {
      path,
      visitorInfo: queryVisitorInfo,
      language,
      salesAdmission: (queryVisitorInfo?.country || 'at').toUpperCase(),
      searchString: String(routeProps.location.query?.search || ''),
      specialPageId: getSpecialPageId(effectiveVisitorInfoType),
    };
  }, [routeProps.location.pathname, routeProps.location.query?.search, isIndex, queryVisitorInfo, lang, effectiveVisitorInfoType]);

  // Fetch data using useLazyLoadQuery.
  // 'store-and-network' renders cached data immediately (no Suspense flicker)
  // while re-fetching in the background. This prevents the brief flash of the
  // loading fallback when navigating between pages (e.g., funds list → fund detail).
  const data = useLazyLoadQuery<SiteQueryContainerQuery>(SiteQuery, variables, {
    fetchPolicy: 'store-and-network',
  });

  // Handle null data - this shouldn't happen if the query succeeds
  if (!data.site || !data.user) {
    // biome-ignore lint/suspicious/noConsole: This is an error message
    console.error('[SiteQueryContainer] Missing data - site:', data.site, 'user:', data.user);
    // Import ErrorPage dynamically to avoid circular dependency
    const ErrorPage = require('../page/errorPage').default;
    return <ErrorPage error={{ statusCode: 500, message: 'Failed to load site data' }} />;
  }

  // Render Site component with fetched data.
  // onVisitorInfoConfirmed re-reads sessionStorage here so the query re-fetches
  // with the visitor info the user just provided (see visitorInfoRevision above).
  return (
    <Site
      site={data.site}
      user={data.user}
      location={routeProps.location}
      visitorInfo={gatingVisitorInfo}
      visitorInfoType={effectiveVisitorInfoType}
      needsNavigation={needsNavigation}
      lang={variables.language}
      onVisitorInfoConfirmed={refreshVisitorInfo}
    />
  );
}

export default SiteQueryContainer;
